<script>
import Validation from '@/plugins/validation'
import { Input, AutocompleteSelect, Checkbox } from '@/components/formelements'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'SiteFilter',
  props: ['id'],
  data() {
    return {
      ...Validation,
      dialog: false,
      tableLoading: false,
      searched: false
    }
  },
  async mounted() {
    this.$store.dispatch('siteFilter/RESET')
    if (this.id != 0) await this.$store.dispatch('siteFilter/GET_PROMO_CHILD_TYPES')
  },
  components: {
    Input,
    AutocompleteSelect,
    Checkbox,
  },
  computed: {
    ...mapState({
      siteFilterResults: state => state.siteFilter.siteFilterResults,
      tableHeaders: state => state.siteFilter.tableHeaders,
      siteTypes: state => state.siteFilter.siteTypes,
      siteCreated: state => state.siteFilter.siteCreated,
      orgRetailerInfo: state => state.retailer.orgRetailerInfo,
      retailerWithNoPretailer: state => state.siteFilter.retailerWithNoPretailer,
      siteSearchID: state => state.siteFilter.siteID,
      siteCode: state => state.siteFilter.siteCode,
      createSiteCode: state => state.siteFilter.createSiteCode
    }),
    ...mapGetters({
      promoChildTypes: 'siteFilter/promoChildTypes',
      siteTypeItems: 'siteFilter/siteTypeItems'
    }),
    includeRetailers(){
      return [
        () => {
          return !(this.retailerWithNoPretailer && (this.siteSearchID == '' && this.siteCode == '')) || 'Site ID or Site Code must be entered in order to include retailers w/o Pretailers'
        },
      ]
    },
    noRetailerRule() {
      return [
        () => {
          return !((this.siteSearchID == '') && (this.siteCode == '')) || 'One field must be entered'
        },
        () => {
          return !((this.siteSearchID != '') && (this.siteCode != '')) || 'Only one field can be entered'
        }
      ]
    },
  },
  methods: {
    async save(){
      if(this.$refs.newSiteForm.validate()) {
        await this.$store.dispatch('ui/SHOW_CONFIRM_DIALOG', {
          header: 'Are you sure?',
          message: `Do you want to create new Site Code <span class="font-weight-black">${ this.createSiteCode }</span>?`,
          action: 'siteFilter/INSERT_SITE',
          data: null,
          nextTickAction: '',
          nextTickData: {},
        })
      }
    },
    typeID(id){
      const type = this.siteTypes.filter( type => type.ID == id)
      return type[0].Name
    },
    async searchSite() {
      if(this.$refs.searchForm.validate()) {
        this.tableLoading = true
        await this.$store.dispatch('siteFilter/FIND_SITE', { retailerId: this.id , itemsPerPage: 10, page: 1 })
        this.tableLoading = false
        this.searched = true
      }
    },
    async pagination(e) {
      if(!this.searched) return
      this.tableLoading = true
      let index = ( e.page * e.itemsPerPage ) - e.itemsPerPage + 1
      await this.$store.dispatch('siteFilter/FIND_SITE', { retailerId: this.id, itemsPerPage: e.itemsPerPage, page: index })
      this.tableLoading = false
    }
  }
}
</script>

<template>
  <v-form ref="searchForm">
    <v-container fluid>
      <v-row v-if="this.id != 0" >
        <router-link style="text-decoration: none" class="pa-5" :to="{ name: 'Retailer', params: { id: this.id } }"> 
          Retailer: {{ orgRetailerInfo.Name }} ({{ this.id }}) 
        </router-link>
      </v-row>
      <v-row v-if="id != 0" justify="center">
        <v-col  cols="12" sm="8">
          <Input form="siteFilter" field="siteID" :rules="[...includeRetailers]" placeholder="" label="Site ID" />
        </v-col>
      </v-row>
      <v-row v-else justify="center">
        <v-col  cols="12" sm="8">
          <Input form="siteFilter" field="siteID" :rules="[...noRetailerRule]" placeholder="" label="Site ID" />
        </v-col>
      </v-row>
      <v-row v-if="id != 0" justify="center">
        <v-col  cols="12" sm="8">
          <Input form="siteFilter" field="siteCode" :rules="[...includeRetailers]" placeholder="" label="Site Code 'Like'" />
        </v-col>
      </v-row>
      <v-row v-else justify="center">
        <v-col  cols="12" sm="8">
          <Input form="siteFilter" field="siteCode" :rules="[...noRetailerRule]" placeholder="" label="Site Code 'Like'" />
        </v-col>
      </v-row>
      <v-row v-if="id != 0" justify="center">
        <v-col  cols="12" sm="8">
          <Checkbox form="siteFilter" field="retailerWithNoPretailer" placeholder="" label="Include Retailer Sites with no Pretailers" />
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-col class="d-flex mb-6" cols="12" sm="2" >
          <v-btn @click="searchSite" width="125px" depressed color="accent">
            Search<v-icon right>mdi-magnify</v-icon>
          </v-btn>
        </v-col>
        <v-col v-if="id != 0" class="d-flex mb-6" cols="12" sm="2" >
          <v-dialog persistent v-model="dialog" width="800">
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" @click="$store.dispatch('siteFilter/RESET_NEW_SITE')" depressed color="accent">
                <v-icon left>mdi-plus-box</v-icon>Create New Site
              </v-btn>
            </template>
            <v-form ref="newSiteForm">
              <v-card>
                <v-card-title>
                  <h1 class="primary--text"> Create New Site </h1>
                </v-card-title>
                <v-row>
                  <v-col cols="12">
                    <v-card-text>
                      Site ID: 
                      <router-link v-if="siteCreated.successful" :to="{ name: 'Site' , params: { id: orgRetailerInfo.ID, siteid: siteCreated.newID } }">
                        {{ siteCreated.newID }}
                      </router-link> 
                    </v-card-text>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-card-text>
                      <Input form="siteFilter" field="createSiteCode" :rules="[...required]" placeholder="" label="Site Code" />
                    </v-card-text>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-card-text>
                      <Input form="siteFilter" field="siteDescription" placeholder="" label="Site Description" />  
                    </v-card-text>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-card-text>
                      <AutocompleteSelect form="siteFilter" field="selectedType" :rules="[...required]" :items="siteTypeItems" placeholder="" label="Site Type" />  
                    </v-card-text>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-card-text>
                      <AutocompleteSelect form="siteFilter" field="selectedPromocode" :items="promoChildTypes" placeholder="" label="Default Promotion Code" />  
                    </v-card-text>
                  </v-col>
                </v-row>
                <v-card-actions class="d-flex justify-end px-5 pb-5">
                  <v-btn @click="dialog = false" width="100" color="secondary">
                    Close
                  </v-btn>
                  <v-btn :disabled="siteCreated.successful" @click="save" width="125" color="primary">
                    <v-icon left>mdi-content-save</v-icon>Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </v-dialog>
        </v-col>
      </v-row>
      <v-row >
        <v-col cols="12">
          <v-data-table
            :server-items-length="$store.state.siteFilter.totalOnServer"
            :headers="tableHeaders"
            :items="siteFilterResults"
            :items-per-page="10"
            @update:options="pagination"
            :loading="tableLoading"
            :footer-props="{ 'items-per-page-options': [ 10, 25 , 50], 'show-first-last-page': true } "
            
          >
            <template v-slot:item.ID=" { item }" >
              <span>
                <router-link :to="{ name: 'Site' , params: { id: id, siteid: item.ID } }">
                  {{ item.ID }}
                </router-link>
              </span>
            </template>
            <template v-slot:item.TypeID=" { item }" >
              <span>
                {{ typeID(item.TypeID) }}
              </span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
